/**
 * Каталог сфер: восемь встроенных плюс созданные человеком.
 *
 * Раньше сферы были константой, и весь интерфейс обращался к таблице `SPHERES`
 * напрямую. Жизнь шире восьми ячеек — «Учёба сына», «Дача», «Волонтёрство»
 * не ложатся ни в одну из них, и всё это утекало в «Развитие», превращая
 * колесо баланса в свалку.
 *
 * Здесь оба источника сведены к одному виду. Дальше по приложению ходит
 * `SphereInfo` — подписи и готовый цвет, — и ни один экран не знает, встроенная
 * это сфера или своя.
 */

import {
  type BuiltInSphereId,
  type SphereId,
  SPHERE_LIST,
  SPHERES,
  sphereColor,
} from '@/shared/config/spheres';
import type { CustomSphere } from '@/shared/db/schema';

import { paletteColor } from './palette';

export interface SphereInfo {
  id: SphereId;
  title: string;
  short: string;
  hint: string;
  emoji: string;
  /** Готовое CSS-значение: у встроенных — переменная темы. */
  color: string;
  /** Класс подписи поверх заливки этим цветом. */
  onAccentClass: string;
  /** Свою сферу можно переименовать и удалить, встроенную — нет. */
  isCustom: boolean;
}

/** Каталог: список в порядке отображения и поиск по ключу. */
export interface SphereCatalog {
  list: SphereInfo[];
  get: (id: SphereId) => SphereInfo;
}

function fromBuiltIn(id: BuiltInSphereId): SphereInfo {
  const meta = SPHERES[id];
  return {
    id,
    title: meta.title,
    short: meta.short,
    hint: meta.hint,
    emoji: meta.emoji,
    color: sphereColor(id),
    onAccentClass: meta.onAccentClass,
    isCustom: false,
  };
}

export function fromCustom(sphere: CustomSphere): SphereInfo {
  return {
    id: sphere.id,
    title: sphere.title,
    short: sphere.short || sphere.title,
    hint: sphere.hint,
    emoji: sphere.emoji,
    color: paletteColor(sphere.color),
    // Палитра своих сфер намеренно тёмная: по любому её цвету белый читается.
    onAccentClass: 'text-white',
    isCustom: true,
  };
}

/** Встроенные сферы — каталог по умолчанию, пока база не прочитана. */
export const BUILT_IN_SPHERES: SphereInfo[] = SPHERE_LIST.map((meta) => fromBuiltIn(meta.id));

/**
 * Заглушка для ссылки в никуда.
 *
 * Задача может ссылаться на сферу, которой больше нет: удаление переносит
 * записи, но между двумя устройствами есть зазор, а старая выгрузка данных
 * может прийти вообще из другой версии. Падать на этом нельзя — но и
 * притворяться, что сфера есть, тоже: пусть будет видно, что ссылка потерялась.
 */
function unknownSphere(id: SphereId): SphereInfo {
  return {
    id,
    title: 'Сфера удалена',
    short: 'Без сферы',
    hint: '',
    emoji: '·',
    color: 'var(--kairo-text-subtle)',
    onAccentClass: 'text-white',
    isCustom: false,
  };
}

export function buildCatalog(custom: readonly CustomSphere[]): SphereCatalog {
  const list = [
    ...BUILT_IN_SPHERES,
    ...[...custom]
      .filter((sphere) => sphere.deletedAt === null)
      .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title, 'ru'))
      .map(fromCustom),
  ];

  const byId = new Map(list.map((sphere) => [sphere.id, sphere]));

  return { list, get: (id) => byId.get(id) ?? unknownSphere(id) };
}

/** Каталог из одних встроенных — значение по умолчанию и опора тестов. */
export const DEFAULT_CATALOG: SphereCatalog = buildCatalog([]);
